/* eslint-disable @typescript-eslint/naming-convention */
import { put, call, takeLatest, select } from 'redux-saga/effects';
import { initialize } from 'redux-form';
import moment from 'moment';

import { unleashHasFeature } from 'mf-shared/utils/featureFlags';
import * as act from '../../actions';
import {
  INVENTORY_SUMMARY_PAGE_REQUEST,
  INVENTORY_SUMMARY_SUMMARY_REQUEST,
  INVENTORY_SUMMARY_ON_CHANGE_OUTLET_IDS,
  INVENTORY_SUMMARY_ON_CHANGE_DATE,
  INVENTORY_SUMMARY_ON_CHANGE_KEYWORD,
  INVENTORY_SUMMARY_EXPORT_SUMMARY,
  INVENTORY_SUMMARY_ON_CHANGE_NAMESPACE,
  INVENTORY_SUMMARY_INGREDIENT_INVENTORY_SUMMARY_REQUEST,
  INVENTORY_SUMMARY_EXPORT_FAILED,
} from '../../actions/inventory/summary/constants';
import * as actInvSummary from '../../actions/inventory/summary';
import * as actNotif from '../../actions/notifications';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';
import { subscriptionStatus } from '../../actions/inventory/ingredientSubscription';
import { NAMESPACE_ITEMS } from '../../actions/inventory/constants';

import * as apiOutlets from '../../api/outlets';
import * as apiInvSummary from '../../api/inventory/summary';
import * as apiGetFilteredDate from '../../api/get-filtered-date';
import * as apiCurrentInventory from '../../api/currentInventoryDropdown';
import { getSubscriptionStatus } from '../../api/feature/getSubscriptionStatus';
import * as apiIngInvSummary from '../../api/ingredientInventory';
import * as apiInventorySummary from '../../api/inventorySummary';
import { setCurrentInventoryDropdownFromLocalStorage } from '../../utils/currentInventoryDropdown';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapInventorySummary from '../../clevertap/inventory/summary';

const getState = (state) => state;

function mapItemsToRowItems(items) {
  const rows: any[] = [];
  items.forEach((item) => {
    if (item.has_item_variants) {
      // push parent
      rows.push({
        item_name: item.item_name,
        category: item.category,
        outlet: item.outlet,
        isParent: true,
        hasChildren: true,
      });
      item.item_variants.forEach((itemVariant) => {
        rows.push({
          ...itemVariant,
          isParent: false,
        });
      });
    } else {
      rows.push({
        ...item.item_variants[0],
        item_name: item.item_name,
        category: item.category,
        outlet: item.outlet,
        isParent: true,
        hasChildren: false,
      });
    }
  });
  return rows;
}

export function* summaryRequestFlow(request) {
  const {
    payload: { params, previousItems, previousRowItems },
  } = request;

  yield put(act.showProgressBar());
  try {
    // TODO: Remove unleash flag https://go-jek.atlassian.net/browse/CEPP-1230
    const { isEnabled } = yield select(
      unleashHasFeature('backoffice-inventory-summary-shadow-proxy')
    );

    let resSummary;
    if (isEnabled) {
      const headers = yield call(getHeaders);
      resSummary = yield call(
        apiInventorySummary.getItemSummary,
        params,
        headers
      );
    } else {
      resSummary = yield call(apiInvSummary.getSummary, params);
    }

    const items = resSummary.data.results;
    const newItems = previousItems.concat(items);
    const newRowItems = previousRowItems.concat(mapItemsToRowItems(items));

    yield put(actInvSummary.setItems(newItems));
    yield put(actInvSummary.setRowItems(newRowItems));
    yield put(actInvSummary.setCurrentPage(params.page));
    yield put(actInvSummary.setTotalPage(resSummary.data.total_pages));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* pageRequest() {
  // reset...
  yield put(actInvSummary.setItems([]));
  yield put(actInvSummary.setRowItems([]));
  yield put(actInvSummary.setIngredients([]));
  // will be set false by summary request saga
  yield put(showPageLoadingSpinner(true));
  yield put(act.showProgressBar());
  try {
    const response = yield call(apiOutlets.outlets);
    yield put(actInvSummary.setOutlets(response.data));

    const resGetFilteredDate = yield call(apiGetFilteredDate.getFilteredDate);
    const startDate = moment(resGetFilteredDate.data.start_date);
    const endDate = moment(resGetFilteredDate.data.end_date);

    const resCurrentInventoryDropdown = yield call(
      apiCurrentInventory.getCurrentInventoryDropdown
    );
    const currentInventoryDropdown =
      resCurrentInventoryDropdown.data.current_inventory_dropdown;

    setCurrentInventoryDropdownFromLocalStorage(currentInventoryDropdown);

    const outletIds = getSelectedOutletIdFromLocalStorage();
    yield put(
      initialize('search', {
        outletIds,
        date: { startDate, endDate },
        namespace: currentInventoryDropdown,
      })
    );
    clevertapInventorySummary.inventorySummaryStart({
      outletIds,
      startDate,
      endDate,
      namespace: currentInventoryDropdown,
    });

    if (currentInventoryDropdown !== 'ingredients') {
      yield put(
        actInvSummary.summaryRequest(
          {
            page: 1,
            start_date: resGetFilteredDate.data.start_date,
            end_date: resGetFilteredDate.data.end_date,
          },
          [],
          [],
          currentInventoryDropdown
        )
      );
    } else {
      yield put(
        actInvSummary.ingInvSummaryRequest(
          {
            page: 1,
            start_date: resGetFilteredDate.data.start_date,
            end_date: resGetFilteredDate.data.end_date,
          },
          [],
          currentInventoryDropdown
        )
      );
    }

    yield put(subscriptionStatus());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onChangeOutletIdsFlow(request) {
  yield put(actInvSummary.setRowItems([]));
  yield put(actInvSummary.setIngredients([]));
  // will be set false by summary request saga
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { outletIds, startDate, endDate, namespace },
  } = request;

  yield put(act.showProgressBar());
  try {
    setSelectedOutletIdToLocalStorage(outletIds);
    yield put(
      initialize('search', {
        outletIds,
        date: { startDate, endDate },
        namespace,
      })
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }

  if (namespace !== 'ingredients') {
    yield put(
      actInvSummary.summaryRequest(
        {
          page: 1,
          start_date: moment(startDate).format('YYYY/MM/DD'),
          end_date: moment(endDate).format('YYYY/MM/DD'),
        },
        [],
        []
      )
    );
  } else {
    yield put(
      actInvSummary.ingInvSummaryRequest(
        {
          page: 1,
          start_date: moment(startDate).format('YYYY/MM/DD'),
          end_date: moment(endDate).format('YYYY/MM/DD'),
        },
        []
      )
    );
  }
}

export function* onChangeDate(request) {
  yield put(actInvSummary.setRowItems([]));
  yield put(actInvSummary.setIngredients([]));
  // will be set false by summary request saga
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { keyword, startDate, endDate, namespace },
  } = request;

  if (namespace !== 'ingredients') {
    yield put(
      actInvSummary.summaryRequest(
        {
          page: 1,
          start_date: moment(startDate).format('YYYY/MM/DD'),
          end_date: moment(endDate).format('YYYY/MM/DD'),
          keyword,
        },
        [],
        [],
        namespace
      )
    );
  } else {
    yield put(
      actInvSummary.ingInvSummaryRequest(
        {
          page: 1,
          start_date: moment(startDate).format('YYYY/MM/DD'),
          end_date: moment(endDate).format('YYYY/MM/DD'),
          keyword,
        },
        [],
        namespace
      )
    );
  }
}

export function* onChangeKeyword(request) {
  yield put(actInvSummary.setRowItems([]));
  yield put(actInvSummary.setIngredients([]));
  // will be set false by summary request saga
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { keyword, startDate, endDate, namespace },
  } = request;

  if (namespace !== 'ingredients') {
    yield put(
      actInvSummary.summaryRequest(
        {
          page: 1,
          start_date: moment(startDate).format('YYYY/MM/DD'),
          end_date: moment(endDate).format('YYYY/MM/DD'),
          keyword,
        },
        [],
        []
      )
    );
  } else {
    yield put(
      actInvSummary.ingInvSummaryRequest(
        {
          page: 1,
          start_date: moment(startDate).format('YYYY/MM/DD'),
          end_date: moment(endDate).format('YYYY/MM/DD'),
          keyword,
        },
        [],
        namespace
      )
    );
  }
}

export function* onChangeNamespace(request) {
  yield put(actInvSummary.setRowItems([]));
  yield put(actInvSummary.setIngredients([]));
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { namespace, params },
  } = request;

  yield put(act.showProgressBar());
  try {
    setCurrentInventoryDropdownFromLocalStorage(namespace);
    yield call(getSubscriptionStatus, {
      feature_names: ['Ingredient Inventory'],
    });
    const resCurrentInventoryDropdown = yield call(
      apiCurrentInventory.getCurrentInventoryDropdown
    );
    const currentInventoryDropdown =
      resCurrentInventoryDropdown.data.current_inventory_dropdown;

    setCurrentInventoryDropdownFromLocalStorage(currentInventoryDropdown);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    if (namespace !== 'ingredients') {
      yield put(actInvSummary.summaryRequest(params, [], [], namespace));
    } else {
      yield put(actInvSummary.ingInvSummaryRequest(params, [], namespace));
    }
    yield put(act.hideProgressBar());
  }
}

export function* ingInvSummaryRequest(request) {
  const {
    payload: { params, previousIngredients },
  } = request;
  yield put(act.showProgressBar());
  try {
    // TODO: Remove unleash flag https://go-jek.atlassian.net/browse/CEPP-1230
    const { isEnabled } = yield select(
      unleashHasFeature('backoffice-inventory-summary-shadow-proxy')
    );

    let resSummary;
    if (isEnabled) {
      const headers = yield call(getHeaders);
      resSummary = yield call(
        apiInventorySummary.getIngredientSummary,
        params,
        headers
      );
    } else {
      resSummary = yield call(apiIngInvSummary.getSummary, params);
    }

    const ingredients = resSummary.data.results;
    const newIngredients = previousIngredients.concat(ingredients);

    yield put(actInvSummary.setIngredients(newIngredients));
    yield put(actInvSummary.setCurrentPage(params.page));
    yield put(actInvSummary.setTotalPage(resSummary.data.total_pages));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* exportSummary(request) {
  yield put(act.showLoadingSpinner(true));

  try {
    yield put(actInvSummary.startExportSummary());
    const {
      payload: {
        params: { namespace, ...restParams },
      },
    } = request;
    if (namespace === NAMESPACE_ITEMS) {
      const { data } = yield call(apiInvSummary.getExportSummary, restParams);
      yield put(actInvSummary.setIdAsyncJob(data));
    } else {
      const { data } = yield call(
        apiIngInvSummary.getExportSummary,
        restParams
      );
      yield put(actInvSummary.setIdAsyncJob(data));
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actInvSummary.doneExportSummary());
    yield put(act.showLoadingSpinner(false));
    yield put(actNotif.error(error.message));
  }
}

export function* exportSummaryFailed(error) {
  yield put(act.showLoadingSpinner(false));
  yield put(actNotif.error(error.message));
}

function* getHeaders() {
  const state = yield select(getState);

  const headers = {
    outlet_id: state.form.search.values.outletIds.toString(),
    'X-Outlet-ID': state.outlet.outlets.toString(),
  };
  return headers;
}

export default function* saga() {
  yield takeLatest(INVENTORY_SUMMARY_PAGE_REQUEST, pageRequest);
  yield takeLatest(INVENTORY_SUMMARY_SUMMARY_REQUEST, summaryRequestFlow);
  yield takeLatest(
    INVENTORY_SUMMARY_ON_CHANGE_OUTLET_IDS,
    onChangeOutletIdsFlow
  );
  yield takeLatest(INVENTORY_SUMMARY_ON_CHANGE_DATE, onChangeDate);
  yield takeLatest(INVENTORY_SUMMARY_ON_CHANGE_KEYWORD, onChangeKeyword);
  yield takeLatest(INVENTORY_SUMMARY_EXPORT_SUMMARY, exportSummary);
  yield takeLatest(INVENTORY_SUMMARY_ON_CHANGE_NAMESPACE, onChangeNamespace);
  yield takeLatest(
    INVENTORY_SUMMARY_INGREDIENT_INVENTORY_SUMMARY_REQUEST,
    ingInvSummaryRequest
  );
  yield takeLatest(INVENTORY_SUMMARY_EXPORT_FAILED, exportSummaryFailed);
}
